import { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  DocumentIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import { documentsApi, type SiteDocument, formatFileSize } from '../api/documents';
import { apiClient } from '../api/client';

interface DocumentPreviewProps {
  document: SiteDocument;
  siteId: string;
  onClose: () => void;
}

// Check if mime type is Excel
const isExcelMimeType = (mimeType: string): boolean => {
  return [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
    'application/vnd.ms-excel', // xls
  ].includes(mimeType);
};

// Check if mime type is CSV
const isCsvMimeType = (mimeType: string): boolean => {
  return mimeType === 'text/csv' || mimeType === 'application/csv';
};

// Extended previewable check
const isExtendedPreviewable = (mimeType: string): boolean => {
  return documentsApi.isPreviewable(mimeType) || isExcelMimeType(mimeType) || isCsvMimeType(mimeType);
};

interface SheetData {
  name: string;
  data: string[][];
}

export function DocumentPreview({ document, siteId, onClose }: DocumentPreviewProps) {
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Excel-specific state
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);

  const isPreviewable = isExtendedPreviewable(document.mime_type);
  const isImage = document.mime_type.startsWith('image/');
  const isPdf = document.mime_type === 'application/pdf';
  const isText = document.mime_type.startsWith('text/') && !isCsvMimeType(document.mime_type);
  const isExcel = isExcelMimeType(document.mime_type);
  const isCsv = isCsvMimeType(document.mime_type);
  const isSpreadsheet = isExcel || isCsv;

  useEffect(() => {
    const loadPreview = async () => {
      if (!isPreviewable) {
        setLoading(false);
        return;
      }

      try {
        if (isPdf) {
          // PDFs: use direct URL with token so browser streams progressively
          // No blob download needed — browser PDF viewer handles streaming
          const url = documentsApi.getDirectViewUrl(siteId, document.id);
          setViewUrl(url);
        } else if (isSpreadsheet) {
          // For Excel/CSV files, fetch and parse with SheetJS
          const response = await apiClient.get(
            `/sites/${siteId}/documents/${document.id}/view`,
            { responseType: 'arraybuffer' }
          );

          const workbook = XLSX.read(response.data, { type: 'array' });
          const parsedSheets: SheetData[] = workbook.SheetNames.map((name) => {
            const sheet = workbook.Sheets[name];
            const data = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 });
            return { name, data: data as string[][] };
          });

          setSheets(parsedSheets);
        } else {
          // For other files (images, text), get blob URL
          const url = await documentsApi.getViewUrl(siteId, document.id);
          setViewUrl(url);
        }
      } catch (err) {
        setError('Failed to load document preview');
        console.error('Preview error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPreview();

    // Cleanup blob URLs on unmount (skip direct URLs)
    return () => {
      if (viewUrl && viewUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(viewUrl);
      }
    };
  }, [siteId, document.id, isPreviewable, isSpreadsheet, isPdf]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleDownload = async () => {
    await documentsApi.download(siteId, document.id, document.original_filename);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const activeSheet = sheets[activeSheetIndex];

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className={`bg-mono-900 border border-mono-700 rounded-lg flex flex-col ${
          isFullscreen
            ? 'w-full h-full rounded-none'
            : 'w-[90vw] h-[90vh] max-w-6xl'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-mono-700">
          <div className="flex items-center gap-3 min-w-0">
            <DocumentIcon className="h-5 w-5 text-cyan-400 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-mono-100 font-medium truncate">
                {document.original_filename}
              </h3>
              <p className="text-mono-500 text-xs">
                {formatFileSize(document.file_size)} • {document.mime_type}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="p-2 text-mono-400 hover:text-cyan-400 hover:bg-mono-800 rounded transition-colors"
              title="Download"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
            </button>
            <button
              onClick={toggleFullscreen}
              className="p-2 text-mono-400 hover:text-cyan-400 hover:bg-mono-800 rounded transition-colors"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? (
                <ArrowsPointingInIcon className="h-5 w-5" />
              ) : (
                <ArrowsPointingOutIcon className="h-5 w-5" />
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-mono-400 hover:text-red-400 hover:bg-mono-800 rounded transition-colors"
              title="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Sheet tabs for Excel files */}
        {isSpreadsheet && sheets.length > 1 && (
          <div className="flex items-center gap-1 px-4 py-2 border-b border-mono-800 bg-mono-900 overflow-x-auto">
            <button
              onClick={() => setActiveSheetIndex(Math.max(0, activeSheetIndex - 1))}
              disabled={activeSheetIndex === 0}
              className="p-1 text-mono-400 hover:text-mono-200 disabled:opacity-30"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            {sheets.map((sheet, index) => (
              <button
                key={sheet.name}
                onClick={() => setActiveSheetIndex(index)}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  index === activeSheetIndex
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
                    : 'text-mono-400 hover:text-mono-200 hover:bg-mono-800'
                }`}
              >
                {sheet.name}
              </button>
            ))}
            <button
              onClick={() => setActiveSheetIndex(Math.min(sheets.length - 1, activeSheetIndex + 1))}
              disabled={activeSheetIndex === sheets.length - 1}
              className="p-1 text-mono-400 hover:text-mono-200 disabled:opacity-30"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Preview Content */}
        <div className="flex-1 overflow-auto bg-mono-950 p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-mono-400">Loading preview...</div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="text-red-400">{error}</div>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 text-sm rounded hover:bg-cyan-500/30 transition-colors"
              >
                Download Instead
              </button>
            </div>
          ) : !isPreviewable ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <DocumentIcon className="h-16 w-16 text-mono-600" />
              <div className="text-mono-400 text-center">
                <p>Preview not available for this file type</p>
                <p className="text-mono-500 text-sm mt-1">{document.mime_type}</p>
              </div>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 text-sm rounded hover:bg-cyan-500/30 transition-colors flex items-center gap-2"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download File
              </button>
            </div>
          ) : isSpreadsheet && activeSheet ? (
            <div className="overflow-auto h-full">
              <table className="min-w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-mono-800">
                  {activeSheet.data.length > 0 && (
                    <tr>
                      <th className="border border-mono-700 px-3 py-2 text-mono-400 text-xs font-medium text-left bg-mono-850">
                        #
                      </th>
                      {activeSheet.data[0].map((_, colIndex) => (
                        <th
                          key={colIndex}
                          className="border border-mono-700 px-3 py-2 text-mono-300 text-xs font-medium text-left bg-mono-850"
                        >
                          {String.fromCharCode(65 + (colIndex % 26))}
                          {colIndex >= 26 ? Math.floor(colIndex / 26) : ''}
                        </th>
                      ))}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {activeSheet.data.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-mono-900' : 'bg-mono-950'}>
                      <td className="border border-mono-700 px-3 py-1.5 text-mono-500 text-xs">
                        {rowIndex + 1}
                      </td>
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="border border-mono-700 px-3 py-1.5 text-mono-200 max-w-xs truncate"
                          title={String(cell ?? '')}
                        >
                          {cell ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {activeSheet.data.length === 0 && (
                <div className="text-mono-500 text-center py-8">Sheet is empty</div>
              )}
            </div>
          ) : viewUrl ? (
            <>
              {isPdf && (
                <iframe
                  src={viewUrl}
                  className="w-full h-full border-0 rounded"
                  title={document.original_filename}
                />
              )}
              {isImage && (
                <div className="flex items-center justify-center h-full">
                  <img
                    src={viewUrl}
                    alt={document.original_filename}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
              {isText && (
                <iframe
                  src={viewUrl}
                  className="w-full h-full border-0 rounded bg-white"
                  title={document.original_filename}
                />
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
